import { useState } from 'react';
import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { DateTime as DT } from 'luxon';
import { TaskTypeStatus, type TaskCardType } from '@econtractor/types';
import * as apiTask from 'api/apiTask';
import { useAuth } from 'context/AuthContext';
import { useModal } from 'context/ModalContext';
import { getButtonsNoYesDanger } from 'context/helpers/helperModalContext';
import { useToast } from 'context/ToastContext';
import EToolTip from 'components/others/EToolTip';
import { isBeforeTaskStart } from '../shared/helpers/isBeforeTaskStart';
import spinnerTitle from 'components/others/spinnerTitle';
import SvgTrash from 'components/svg/SvgTrash';
import SolarIcon from 'components/icons/SolarIcon';

interface TaskActionsProps {
    task: TaskCardType;
    onTaskUpdated: (task: TaskCardType) => void;
    onRequestResponse: () => void;
    onRequestReject: () => void;
    rejectFormOpen?: boolean;
}

type TaskActionId = 'take' | 'accept' | 'cancel' | 'delete';

const TaskActions = ({
    task,
    onTaskUpdated,
    onRequestResponse,
    onRequestReject,
    rejectFormOpen = false,
}: TaskActionsProps) => {
    const { t } = useTranslation(['Tasks', 'common']);
    const { auth } = useAuth();
    const { showDlg } = useModal();
    const { showToast } = useToast();
    const [busyAction, setBusyAction] = useState<TaskActionId | null>(null);
    const busy = busyAction !== null;

    const userId = Number(auth?.user?.ID || 0);
    const status = Number(task.taskStatus);
    const isExecutor = userId > 0 && userId === Number(task.taskExecutor);
    const isController = userId > 0 && userId === Number(task.taskController);
    const isOwner = userId > 0 && userId === Number(task.userId);
    const isDeleted = Boolean(Number(task.taskDeleted));

    const showTakeInWork = !isDeleted && status === TaskTypeStatus.NEW && isExecutor;
    const tooEarly = Boolean(task.noEarlyStart) && isBeforeTaskStart(task.dateStart);
    const earlyStartLabel = (() => {
        const start = Number(task.dateStart || 0);
        if (!(start > 0)) return '';
        const point = DT.fromMillis(start, { zone: 'local' });
        if (!point.isValid) return '';
        return point.toFormat(task.withTime ? 'dd.MM.yyyy HH:mm' : 'dd.MM.yyyy');
    })();
    const showComplete = !isDeleted && status === TaskTypeStatus.INWORK && isExecutor;
    const showAccept = !isDeleted && status === TaskTypeStatus.COMPLETED && isController;
    const showReject = showAccept;
    const showCancel =
        !isDeleted &&
        (status === TaskTypeStatus.NEW || status === TaskTypeStatus.INWORK) &&
        (isOwner || isController);
    const showDelete =
        !isDeleted && status === TaskTypeStatus.NEW && (isOwner || isController);

    if (!showTakeInWork && !showComplete && !showAccept && !showCancel && !showDelete) {
        return null;
    }

    const runAction = async (
        actionId: TaskActionId,
        action: () => Promise<apiTask.TaskActionResult | null>,
    ) => {
        setBusyAction(actionId);
        try {
            const result = await action();
            if (!result?.stat || !result.task) {
                if (result?.errCode === 'TOO_EARLY') {
                    showToast(t('messTooEarly'), 'warning');
                    return;
                }
                showToast(t('common:messError'), 'danger');
                return;
            }
            onTaskUpdated(result.task);
        } finally {
            setBusyAction(null);
        }
    };

    const confirmDangerAction = (
        actionId: TaskActionId,
        confirmText: string,
        action: () => Promise<apiTask.TaskActionResult | null>,
        withTrashIcon = true,
    ) => {
        const buttons = [...getButtonsNoYesDanger(t)];
        buttons[1] = {
            ...buttons[1],
            handleClick: () => {
                showDlg(false);
                void runAction(actionId, action);
            },
        };

        showDlg({
            type: 'confirm',
            buttons,
            text: (
                <>
                    <h4 className="d-flex pb-2 mb-2 border-bottom text-danger">
                        {withTrashIcon ? (
                            <SvgTrash className="text-danger" />
                        ) : (
                            <SolarIcon name="x-circle" className="fs-4" />
                        )}
                        <span className="ps-2">{confirmText}</span>
                    </h4>
                    {task.taskName ? <h5 className="mb-0">{task.taskName}</h5> : null}
                </>
            ),
        });
    };

    return (
        <div className="d-flex flex-column align-items-end gap-1 ms-auto">
            <div className="d-flex flex-wrap align-items-center justify-content-end gap-2">
            {showTakeInWork &&
                (tooEarly ? (
                    <EToolTip title={t('hintTakeInWorkNotBefore', { date: earlyStartLabel })}>
                        <Button size="sm" variant="info" disabled>
                            <SolarIcon name="play-fill" className="me-1" />
                            {t('btnTakeInWork')}
                        </Button>
                    </EToolTip>
                ) : (
                    <Button
                        size="sm"
                        variant="info"
                        disabled={busy}
                        onClick={() =>
                            void runAction('take', () =>
                                apiTask.updateTaskStatus(task.ID, TaskTypeStatus.INWORK),
                            )
                        }
                    >
                        {spinnerTitle(
                            <>
                                <SolarIcon name="play-fill" className="me-1" />
                                {t('btnTakeInWork')}
                            </>,
                            busyAction === 'take',
                            t('common:buttons.saving'),
                        )}
                    </Button>
                ))}

            {showComplete && (
                <Button size="sm" variant="success" disabled={busy} onClick={onRequestResponse}>
                    <SolarIcon name="check2-all" className="me-1" />
                    {t('btnComplete')}
                </Button>
            )}

            {showAccept && (
                <Button
                    size="sm"
                    variant="success"
                    disabled={busy || rejectFormOpen}
                    onClick={() =>
                        void runAction('accept', () =>
                            apiTask.updateTaskStatus(task.ID, TaskTypeStatus.ACCEPTED),
                        )
                    }
                >
                    {spinnerTitle(
                        <>
                            <SolarIcon name="hand-thumbs-up" className="me-1" />
                            {t('btnAccept')}
                        </>,
                        busyAction === 'accept',
                        t('common:buttons.saving'),
                    )}
                </Button>
            )}

            {showReject && (
                <Button
                    size="sm"
                    variant={rejectFormOpen ? 'warning' : 'outline-warning'}
                    disabled={busy}
                    onClick={onRequestReject}
                >
                    <SolarIcon name="arrow-return-left" className="me-1" />
                    {t('btnReturnToWork')}
                </Button>
            )}

            {showCancel && (
                <Button
                    size="sm"
                    variant="outline-danger"
                    disabled={busy || rejectFormOpen}
                    onClick={() =>
                        confirmDangerAction(
                            'cancel',
                            t('confirmCancel'),
                            () => apiTask.updateTaskStatus(task.ID, TaskTypeStatus.CANCELLED),
                            false,
                        )
                    }
                >
                    {spinnerTitle(
                        <>
                            <SolarIcon name="x-circle" className="me-1" />
                            {t('btnCancel')}
                        </>,
                        busyAction === 'cancel',
                        t('common:buttons.saving'),
                    )}
                </Button>
            )}

            {showDelete && (
                <EToolTip title={t('btnDelete')}>
                    <Button
                        size="sm"
                        variant="outline-danger"
                        className="px-2"
                        disabled={busy || rejectFormOpen}
                        aria-label={t('btnDelete')}
                        onClick={() =>
                            confirmDangerAction('delete', t('confirmDelete'), () =>
                                apiTask.deleteTask(task.ID),
                            )
                        }
                    >
                        {busyAction === 'delete' ? (
                            <span className="spinner-border spinner-border-sm" role="status" />
                        ) : (
                            <SolarIcon name="trash" />
                        )}
                    </Button>
                </EToolTip>
            )}
            </div>
            {showTakeInWork && tooEarly ? (
                <span className="text-muted fs-12 lh-sm text-end">
                    <SolarIcon name="lock" className="me-1" />
                    {t('hintTakeInWorkNotBefore', { date: earlyStartLabel })}
                </span>
            ) : null}
        </div>
    );
};

export default TaskActions;
